import BookingAppearanceControl from './platform/assets/css/bookingAppearanceControl';
import { clearStorageForLogout } from './platform/util/startPage';
import { isReservedBusinessSlug } from "./platform/util/businessSlug";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Box, Toolbar } from "@mui/material";
import jwtDecode from "jwt-decode";
import { images } from "./platform/util/images";
import { UserProvider } from "./platform/components/auth/auth-context";
import CustomerHeader from "./platform/components/headers/customerHeader";
import BusinessHeader from "./platform/components/headers/businessHeader";
import RouteGroups from "./routeGroups";
import BackgroundWrapper from "./platform/util/backGroundWrapper";
import { useDispatch } from "react-redux";
import { setSelectedBusiness } from "./platform/redux/actions/businessActions";
import FloatingCompanyBrand from "./platform/components/common/floatingCompanyBrand";
import Footer from "./platform/components/common/footer";
import i18n from "./platform/util/i18n";
import { getToken, getLoginDestination, decodeSessionToken, setToken as persistToken } from "./platform/util/authUtils";
import { VerticalProvider } from "./platform/verticals/useVertical";
import { syncFloatingBrandPathHistory } from "./platform/components/common/floatingCompanyBrand.helpers";
import { getNormalizedHostname } from "./platform/util/brandDomains";
// src/verticals/useVertical.ts 


function hasChangedFields(prev, next) {
  if (!next || typeof next !== "object") return false;
  return Object.keys(next).some((key) => prev?.[key] !== next?.[key]);
}

function getCareBrandCanonicalPath(pathname) {
  if (pathname === "/care") return "/find";
  if (pathname === "/care/find") return "/find";
  if (pathname.startsWith("/care/city/")) return pathname.replace(/^\/care\/city/, "/city");
  if (pathname.startsWith("/care/state/")) return pathname.replace(/^\/care\/state/, "/state");
  return null;
}


function App() {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currentPath = location.pathname;
  const currentRoute = `${location.pathname}${location.search}${location.hash}`;

  useEffect(() => {
    const host = getNormalizedHostname();
    if (host !== "care.plany365.com") return;
    if (!location.pathname.startsWith("/care")) return;

    const targetPath = getCareBrandCanonicalPath(location.pathname);
    if (!targetPath) return;

    const nextRoute = `${targetPath}${location.search || ""}${location.hash || ""}`;
    const current = `${location.pathname}${location.search || ""}${location.hash || ""}`;
    if (nextRoute !== current) {
      navigate(nextRoute, { replace: true });
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  // -----------------------------
  // 🧠 Token + Auth Management - START
  // -----------------------------

  const [token, setTokenState] = useState(getToken());
  const [fnToExecute, setFnToExecute] = useState(null);
  const [inlineBotOccupiedWidth, setInlineBotOccupiedWidth] = useState(0);

  // Memoized decoded token: Only re-decode if the raw token string changes.
  const memoizedDecodedToken = useMemo(() => {
    try {
      return token ? jwtDecode(token) : null;
    } catch (e) {
      console.error("Error decoding token:", e);
      return null;
    }
  }, [token]);

  // Provider user state: The actual user data provided through context.
  const [providerUser, setProviderUser] = useState(() => memoizedDecodedToken || {});

  // --- Login & Logout Callbacks (DEFINED FIRST) ---
  // These are defined early so they can be used by useEffects below.
  const login = useCallback(
    async (access_token) => {
      const signedInUser = decodeSessionToken(access_token);
      persistToken(access_token);
      // Publish the account with its token so route guards never see the old
      // signed-out account while deciding the post-login destination.
      setProviderUser(signedInUser || {});
      setTokenState(access_token);

      if (fnToExecute) {
        await fnToExecute();
        sessionStorage.removeItem("redirectAfterLogin");
        localStorage.removeItem("redirectAfterLogin");
      } else {

        const savedRedirect =
          sessionStorage.getItem("redirectAfterLogin") ||
          localStorage.getItem("redirectAfterLogin");
        const redirectPath = getLoginDestination(signedInUser, savedRedirect);
        sessionStorage.removeItem("redirectAfterLogin");
        localStorage.removeItem("redirectAfterLogin");
        navigate(redirectPath, { replace: true });
      }
    },
    [fnToExecute, navigate]
  );

  const logout = useCallback(async () => {
    try {
      persistToken(null);
      clearStorageForLogout();
      sessionStorage.clear();
      setTokenState(null); // Clears token, which triggers memoizedDecodedToken and providerUser reset
      if (fnToExecute) await fnToExecute();
      setFnToExecute(null);
    } catch (err) {
      console.error("Logout error:", err);
    }
  }, [fnToExecute]);

  const setFnToExecuteAfterLoginSuccess = useCallback((cb) => {
    setFnToExecute(() => cb);
  }, []);

  const updateProviderUserContext = useCallback((newUserData) => {
    setProviderUser((prev) => {
      if (!hasChangedFields(prev, newUserData)) {
        return prev;
      }
      return { ...prev, ...newUserData };
    });
  }, []);

  // --- Effects (Now `logout` is defined) ---

  // Effect to sync providerUser from memoizedDecodedToken
  useEffect(() => {
    if (memoizedDecodedToken) {
      setProviderUser((prev) => {
        if (!hasChangedFields(prev, memoizedDecodedToken)) {
          return prev;
        }
        return { ...prev, ...memoizedDecodedToken };
      });
    } else {
      if (Object.keys(providerUser).length > 0) {
        setProviderUser({});
      }
    }
  }, [memoizedDecodedToken]);

  // Set selected business in Redux when defaultBusinessId changes in the memoized decoded token.
  useEffect(() => {
    const defaultBusinessId = Number(memoizedDecodedToken?.defaultBusinessId || 0);
    let preferredBusinessId = defaultBusinessId;

    try {
      const persistedBusinessId = Number(localStorage.getItem("selectedBusinessId") || 0);
      if (persistedBusinessId > 0) {
        preferredBusinessId = persistedBusinessId;
      }
    } catch (error) {
      // ignore storage failures
    }

    if (preferredBusinessId > 0) {
      dispatch(setSelectedBusiness(preferredBusinessId));
    }
  }, [memoizedDecodedToken?.defaultBusinessId, dispatch]);

  // Auto logout on token expiry
  useEffect(() => {
    if (memoizedDecodedToken && memoizedDecodedToken.exp < Date.now() / 1000) {
      logout(); // This is now safe, as `logout` is defined above
      console.warn("Token expired — logged out.");
    }
  }, [memoizedDecodedToken, logout]);

  // -----------------------------
  // Language Setting
  // -----------------------------
  useEffect(() => {
    const selectedLanguage = localStorage.getItem("selectedLanguage");
    if (selectedLanguage) {
      i18n.changeLanguage(selectedLanguage);
    }
  }, [i18n]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const initial = Number(window.__planybotInlineOccupiedWidth || 0);
      setInlineBotOccupiedWidth(initial > 0 ? initial : 0);
    }

    const handleInlineBotPanelChange = (event) => {
      const width = Number(event?.detail?.occupiedWidth || 0);
      setInlineBotOccupiedWidth(width > 0 ? width : 0);
    };

    window.addEventListener("planybot:inline-panel-change", handleInlineBotPanelChange);
    return () => {
      window.removeEventListener("planybot:inline-panel-change", handleInlineBotPanelChange);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const latest = Number(window.__planybotInlineOccupiedWidth || 0);
    setInlineBotOccupiedWidth(latest > 0 ? latest : 0);
  }, [currentPath]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    syncFloatingBrandPathHistory(window.sessionStorage, currentRoute);
  }, [currentRoute]);

  // -----------------------------
  // Routing & Rendering Components Memoization
  // -----------------------------

  const memoizedRouteGroupsElement = useMemo(() => (
    <RouteGroups
      providerUser={providerUser}
      decodedToken={memoizedDecodedToken}
      setProviderUser={setProviderUser}
      token={token}
      login={login}
      logout={logout}
      setImage={() => { }}
    />
  ), [
    providerUser,
    memoizedDecodedToken,
    setProviderUser,
    token,
    login,
    logout
  ]);

  const IsBookingRoute = useCallback(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    return (segments[0] === "b" || !isReservedBusinessSlug(segments[0])) && segments.length >= 2;
  }, [location.pathname]);




  const CARE_SLUG_FIRST_RE = /^\/[^/]+\/care(?:\/|$)/;
  const PUBLIC_BUSINESS_HOME_RE = /^\/b\/[^/]+\/?$/;
  const PUBLIC_CARE_BUSINESS_HOME_RE = /^\/b\/[^/]+\/care\/?$/;
  const PUBLIC_BUSINESS_SUBPAGE_RE = /^\/b\/[^/]+\/(?:care\/referral|jobs)(?:\/|$)/;

  const isCareSlugFirst = useMemo(
    () => CARE_SLUG_FIRST_RE.test(currentPath),
    [currentPath]
  );
  const isPublicBusinessHomeRoute = useMemo(
    () => PUBLIC_BUSINESS_HOME_RE.test(currentPath) || (currentPath.split("/").filter(Boolean).length === 1 && !isReservedBusinessSlug(currentPath.split("/")[1])),
    [currentPath]
  );
  const isPublicCareBusinessHomeRoute = useMemo(
    () => PUBLIC_CARE_BUSINESS_HOME_RE.test(currentPath),
    [currentPath]
  );
  const isPublicBusinessSubpageRoute = useMemo(
    () => PUBLIC_BUSINESS_SUBPAGE_RE.test(currentPath),
    [currentPath]
  );


  const pagesWithBackground = useMemo(() => ["/login", "/signup", "/forgotpassword"], []);
  const isAuthRoute = useMemo(() => pagesWithBackground.includes(currentPath), [pagesWithBackground, currentPath]);
  const isReferrerShellRoute = useMemo(
    () =>
      currentPath === "/referrals" ||
      currentPath.startsWith("/referrals/") ||
      currentPath === "/referrer" ||
      currentPath.startsWith("/referrer/"),
    [currentPath]
  );
  const isReferrerExperienceRoute = useMemo(
    () => isReferrerShellRoute || currentPath === "/care/request",
    [currentPath, isReferrerShellRoute]
  );
  const isCareBrowseExperienceRoute = useMemo(
    () =>
      currentPath === "/care/find" ||
      currentPath === "/find" ||
      currentPath.startsWith("/care/city/") ||
      currentPath.startsWith("/city/") ||
      currentPath.startsWith("/care/state/") ||
      currentPath.startsWith("/state/"),
    [currentPath]
  );
  const isProviderMarketingRoute = useMemo(
    () =>
      currentPath === "/care-directory" ||
      currentPath.startsWith("/care-directory/") ||
      currentPath === "/resident-management" ||
      currentPath.startsWith("/resident-management/") ||
      currentPath === "/emar",
    [currentPath]
  );
  const nonContainerWrapper = useMemo(() => [
    "/",
    "/login",
    "/signup",
    "/forgotpassword",
    "/biz/calendar",
    "/biz/printview",
    "/care",
    "/find",
    "/care-directory",
    "/resident-management",
    "/emar",
    "/referrals",
    "/referrer",
    "/rentals",
    "/booking",
    "/booking/start",
  ], []);
  const isBookingRoute = IsBookingRoute();
  const isBusinessBookingRoute = useMemo(() => /^\/\d+\/[^/]+/.test(currentPath), [currentPath]);
  const isBusinessRoute = useMemo(() => currentPath.startsWith("/biz/") || ["/biz", "/about", "/privacy", "/faq", "/terms", "/security", "/web-design"].includes(currentPath), [currentPath]);
  const isCustomerHeaderVisible = useMemo(() =>
    // keep your existing logic…
    currentPath.startsWith("/category/")
    || currentPath.startsWith("/booking/category/")
    || isPublicBusinessHomeRoute
    || isPublicCareBusinessHomeRoute
    || isPublicBusinessSubpageRoute
    // …but explicitly show header for slug-first care routes
    || isCareSlugFirst
    || (
      !isBusinessRoute
      && !pagesWithBackground.includes(currentPath)
      && !isBusinessBookingRoute
      && !isBookingRoute
    ),
    [
      currentPath,
      isBusinessRoute,
      pagesWithBackground,
      isBusinessBookingRoute,
      isBookingRoute,
      isPublicBusinessHomeRoute,
      isPublicCareBusinessHomeRoute,
      isPublicBusinessSubpageRoute,
      isCareSlugFirst
    ]
  );
  const isNonContainerWrapper = useMemo(
    () =>
      nonContainerWrapper.includes(currentPath) ||
      isReferrerShellRoute ||
      isCareBrowseExperienceRoute,
    [nonContainerWrapper, currentPath, isReferrerShellRoute, isCareBrowseExperienceRoute]
  );
  const isEmbedRoute = useMemo(() => currentPath.startsWith("/embed"), [currentPath]);
  const isCalendarRoute = useMemo(() => currentPath.startsWith("/biz/calendar"), [currentPath]);
  const shouldReserveInlineBotSpace = useMemo(
    () => isBusinessRoute && !isCalendarRoute && inlineBotOccupiedWidth > 0,
    [isBusinessRoute, isCalendarRoute, inlineBotOccupiedWidth]
  );

  const renderedBusinessRoutes = useMemo(() => {
    const content = isNonContainerWrapper ? memoizedRouteGroupsElement : (
      <Container
        maxWidth="xl"
        sx={{
          padding: 0,
          marginTop: isEmbedRoute
            ? 0
            : (isBusinessRoute || isAuthRoute ? 0 : "100px"),
        }}
      >
        {memoizedRouteGroupsElement}
      </Container>
    );

    return (
      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          pt:
            isReferrerShellRoute || isProviderMarketingRoute
              ? { xs: "72px", sm: "82px" }
              : 0,
          pr: shouldReserveInlineBotSpace
            ? `${Math.max(0, inlineBotOccupiedWidth - 12)}px`
            : 0,
          transition: "padding-right 220ms ease",
        }}
      >
        {content}
      </Box>
    );
  }, [
    isNonContainerWrapper,
    isEmbedRoute,
    isBusinessRoute,
    isAuthRoute,
    memoizedRouteGroupsElement,
    shouldReserveInlineBotSpace,
    inlineBotOccupiedWidth,
    isReferrerShellRoute,
    isProviderMarketingRoute,
  ]);

  const HeaderComponent = useMemo(() => {
    if (isEmbedRoute) return null;
    if (isBusinessRoute) {
      return (
        <BusinessHeader />
      );
    }
    if (isCustomerHeaderVisible) {
      return (
        <CustomerHeader />
      );
    }
    return <FloatingCompanyBrand />;
  }, [
    isEmbedRoute,
    isBusinessRoute,
    isCustomerHeaderVisible,
  ]);

  const footer = useMemo(() => isEmbedRoute ? null : <Footer />, [isEmbedRoute]);
  return (
    <>
      <BackgroundWrapper
        id="main"
        currentPath={currentPath}
        backgroundImage=""
        images={images}
      >
        <UserProvider
          value={{
            login,
            logout,
            providerUser,
            setFnToExecuteAfterLoginSuccess,
            fnToExecute: null,
            updateProviderUser: updateProviderUserContext,
          }}
        >
          <VerticalProvider>
            {HeaderComponent}
            {!isEmbedRoute && !isBusinessRoute && !isAuthRoute && <Toolbar sx={{ py: 1 }} />}
            <BookingAppearanceControl />
            {renderedBusinessRoutes}
          </VerticalProvider>
        </UserProvider>
      </BackgroundWrapper>
      {footer}
    </>
  );
}

export default App;
